const QueryFormatAndExamples = (): React.JSX.Element => {
   return (
      <details className="note query-format" style={{ marginTop: '1em', marginBottom: '1em' }}>
         <summary>Query format & examples</summary>
         <h4>Query Format</h4>
         <div className="highlight">
            <pre>
               <code>&lt;pdb_id&gt; &lt;chains&gt; &lt;ligand&gt; &lt;position&gt; # comment</code>
            </pre>
         </div>
         <ul>
            <li>
               <code>pdb_id</code>: This is the 4-character code of a PDB protein structure. This argument is obligatory
               and only 1 PDB ID can be input per line. (i.e. “1a73” or “3fav” or “3FAV”). If it is the only argument
               (because the user does not know the ligand that binds to the structure or is using "reverse search", it
               will trigger automatic detection of ligands in the structure.
            </li>
            <li>
               <code>chains</code>: A single chain or multiple chains separated by commas (without whitespace), or “ALL”
               or “” in the case of all chains (i.e. “A” or “A,C,D” or “ALL” or “”). This argument is obligatory if the
               user intends to provide any argument after that (i.e. ligands or position).
            </li>
            <li>
               <code>ligand</code>: A single ligand, multiple ligands separated by commas (without whitespace), or no
               ligands can be input per line (i.e. “HEM” or “hem” or “ATP” or “ZN” or “HEM,ATP,ZN”). This argument is
               non-obligatory, if omitted, the user should activate the automatic detection of the ligands in the
               structure from the available option, unless the user is starting with an apo structure, in which case
               they will need to activate the reverse mode (search for holo from apo). Note: if planning to specify the
               position argument, you cannot use more than one ligand per query.
            </li>
            <li>
               <code>position</code>: This argument is an integer (i.e. “260” or “1”). It refers to the PDB index of the
               previously specified ligand or binding residue. This argument can only be specified when there is one
               ligand or residue specified.
            </li>
         </ul>
         All elements except <code>pdb_id</code> are optional.
         <h4>Example Query</h4>
         <div className="highlight">
            <pre>
               <code>
                  <span className="k">1a73 A ZN 201</span> # consider ZN ligand in position 201 in chain A of 1a73{' '}
               </code>
            </pre>
         </div>
         <p>
            The application will fetch the structure 1a73, get chain A, and look for zinc+2 (ZN) ligand in position 201
            of the sequence to verify the input argument. If ZN is found in chain A and position 201 of 1a73 (1a73A), it
            will retrieve all other known chains that belong to the same protein with 1a73A, it will align them with
            1a73A and look for ZN (and also other ligands) at the superimposed binding site of ZN in 1a73A. If it finds
            protein chains with ZN, it will list them as HOLO, if the superimposed site is empty of ligands, the chain
            will be listed as APO. If another ligand is detected on that site instead of ZN, the chain will be listed as
            APO or HOLO, depending on the value of --lig_free_sites parameter (if the user wants APO with no other
            ligands there, it will be listed as HOLO, and if the user does not mind other ligands in this binding site,
            it will be listed as APO).
         </p>
         Example of an alternative query that leads to the same result as the previous example:
         <div className="highlight">
            <pre>
               <code>
                  <span className="k">1a73 A HIS 134</span> # consider ligands near residue HIS134 in chain A of 1a73
                  (the detected ligand will be ZN 201 in chain A)
               </code>
            </pre>
         </div>
         <h4>More examples</h4>
         <div className="highlight">
            <pre id="">
               <span></span>
               <code>
                  <span className="k">1a73 A,B ZN </span> # consider ZN ligands in chains A and B of 1a73
                  <span className="k">1a73 ALL ZN </span> # consider ZN ligands in all chains of 1a73
                  <span className="k">1a73 </span> # find and consider all ligands in all chains of 1a73
                  <span className="k">1a73 A </span> # find and consider all ligands in chain A of 1a73
                  <span className="k">1a73 A ZN,MG</span> # consider ZN and MG ligands in chain A of 1a73
                  <span className="k">1aax </span> # protein tyrosine phosphatase - long search
                  <span className="k">4est </span> # porcine pancreatic elastase
               </code>
            </pre>
         </div>
      </details>
   );
};

export default QueryFormatAndExamples;
